import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Send, Flag, CheckCircle2 } from "lucide-react";
import VSLogo from "@/components/VSLogo";

export default function Assessment() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(()=>{
    api.get(`/assessments/${aid}`).then(r=>{
      setA(r.data);
      setRemaining((r.data.duration_min||20)*60);
      if (r.data.status === "completed") {
        toast.info("This assessment is already completed.");
        nav("/candidate/dashboard");
      }
    }).catch(e=>{ toast.error("Assessment not found"); nav("/candidate/dashboard"); });
  },[aid, nav]);

  const submit = React.useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/assessments/${aid}/submit`, { answers });
      setResult(data);
      toast.success(`Assessment submitted — ${data.percentage}%`);
    } catch(e){ toast.error("Submit failed"); }
    finally { setSubmitting(false); }
  }, [aid, answers, submitting]);

  useEffect(()=>{
    if (!a || result) return;
    const t = setInterval(()=>{
      setRemaining(r => {
        if (r <= 1) { clearInterval(t); submit(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [a, result, submit]);

  const mm = String(Math.floor(remaining/60)).padStart(2,"0");
  const ss = String(remaining%60).padStart(2,"0");
  const answered = useMemo(()=>Object.keys(answers).length, [answers]);
  const total = a?.questions?.length || 0;

  if (!a) return <div className="p-10 text-slate-500">Loading assessment...</div>;

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-3xl mx-auto p-6 pt-12">
          <Card className="p-10 border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600"/>
            </div>
            <h1 className="font-display text-3xl font-bold text-[#0A2540] mt-4">Application Submitted Successfully</h1>
            <p className="text-slate-500 mt-2">Application ID: <span className="font-mono">{result.application_id}</span></p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Score</div><div className="font-display text-2xl font-bold text-[#0A2540] mt-1">{result.score}/{total}</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Percentage</div><div className="font-display text-2xl font-bold text-[#0A2540] mt-1">{result.percentage}%</div></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Result</div><div className={`font-display text-2xl font-bold mt-1 ${result.passed ? "text-emerald-600" : "text-amber-600"}`}>{result.passed ? "Passed" : "Under Review"}</div></div>
            </div>
            {result.skillwise?.length>0 && <div className="mt-6 text-left">
              <div className="font-semibold text-[#0A2540] mb-2">Skill breakdown</div>
              <div className="space-y-2">
                {result.skillwise.map(s=>(
                  <div key={s.skill}>
                    <div className="flex justify-between text-sm"><span>{s.skill}</span><span className="font-semibold">{s.percentage}%</span></div>
                    <Progress value={s.percentage} className="h-1.5"/>
                  </div>
                ))}
              </div>
            </div>}
            <Button onClick={()=>nav("/candidate/dashboard")} className="mt-8 bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="result-go-dashboard">Go to Dashboard</Button>
          </Card>
        </div>
      </div>
    );
  }

  const q = a.questions[idx];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <VSLogo size={32}/>
            <div>
              <div className="text-xs text-slate-500">Skill Assessment</div>
              <div className="font-semibold text-[#0A2540]">{a.job_title}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md font-mono font-semibold ${remaining<60?"bg-red-100 text-red-700":"bg-slate-100 text-slate-700"}`} data-testid="asmt-timer">
              <Clock className="w-4 h-4"/>{mm}:{ss}
            </div>
            <div className="text-sm text-slate-500">{answered}/{total}</div>
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="asmt-submit-top">
              <Send className="w-4 h-4 mr-1.5"/>{submitting?"Submitting...":"Submit"}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <Card className="p-6 md:p-8 border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{q.skill}</Badge>
              <span className="text-xs text-slate-400">Question {idx+1} of {total}</span>
            </div>
            <h2 className="font-display text-lg md:text-xl font-semibold text-[#0A2540] mt-3" data-testid={`q-${idx}-text`}>{q.text}</h2>
            <RadioGroup value={answers[q.id]||""} onValueChange={(v)=>setAnswers({...answers, [q.id]: v})} className="mt-6 space-y-3">
              {q.options.map(opt=>(
                <label key={opt.id} className={`flex items-center gap-3 border rounded-md p-3 cursor-pointer transition-colors ${answers[q.id]===opt.id?"border-[#2563EB] bg-blue-50":"border-slate-200 hover:border-slate-300"}`} data-testid={`q-${idx}-${opt.id}`}>
                  <RadioGroupItem value={opt.id} id={`${q.id}-${opt.id}`}/>
                  <Label htmlFor={`${q.id}-${opt.id}`} className="cursor-pointer flex-1 font-normal">{opt.text}</Label>
                </label>
              ))}
            </RadioGroup>
            <div className="mt-8 flex flex-wrap gap-2 justify-between">
              <Button variant="outline" onClick={()=>setIdx(Math.max(0,idx-1))} disabled={idx===0} data-testid="asmt-prev"><ChevronLeft className="w-4 h-4 mr-1"/>Previous</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={()=>setMarked({...marked, [q.id]: !marked[q.id]})} data-testid="asmt-mark"><Flag className="w-4 h-4 mr-1"/>{marked[q.id]?"Unmark":"Mark for review"}</Button>
                <Button onClick={()=>setIdx(Math.min(total-1,idx+1))} disabled={idx===total-1} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="asmt-next">Next <ChevronRight className="w-4 h-4 ml-1"/></Button>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-4 border-slate-200 h-fit">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Questions</div>
          <div className="grid grid-cols-5 lg:grid-cols-4 gap-2">
            {a.questions.map((qq,i)=>{
              const isAns = !!answers[qq.id];
              const isMarked = !!marked[qq.id];
              const isCurrent = i === idx;
              const cls = isCurrent ? "bg-[#0A2540] text-white border-[#0A2540]" :
                isMarked ? "bg-amber-100 text-amber-800 border-amber-300" :
                isAns ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-slate-600 border-slate-200";
              return <button key={qq.id} onClick={()=>setIdx(i)} className={`h-9 rounded-md border text-xs font-semibold ${cls}`} data-testid={`asmt-nav-${i}`}>{i+1}</button>;
            })}
          </div>
          <div className="mt-4 text-xs text-slate-500 space-y-1">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-emerald-200 inline-block"/>Answered</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-200 inline-block"/>Marked</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-white border inline-block"/>Not answered</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
